import type { EditOp, SourcePack } from "@desk/core";
import { captionMatchesGrammar } from "@desk/core";
import {
  OMINOUS_AUDIO_ASSET_ID,
  WARM_AUDIO_ASSET_ID,
} from "./assets.js";

export type SoundTreatment = "warm" | "neutral" | "ominous";

export function soundTreatmentFromOps(ops: EditOp[]): SoundTreatment {
  for (let i = ops.length - 1; i >= 0; i--) {
    const op = ops[i];
    if (!op || op.kind !== "sound") continue;
    if (op.role === "silence" || op.volumeMilli === 0) return "neutral";
    if (op.assetId === OMINOUS_AUDIO_ASSET_ID) return "ominous";
    if (op.assetId === WARM_AUDIO_ASSET_ID) return "warm";
  }
  return "neutral";
}

export function soundOpForTreatment(
  treatment: SoundTreatment,
  packEndTick: number,
  opId: string,
): EditOp {
  switch (treatment) {
    case "warm":
      return {
        kind: "sound",
        opId,
        assetId: WARM_AUDIO_ASSET_ID,
        role: "bed",
        startTick: 0,
        endTick: packEndTick,
        volumeMilli: 600,
      };
    case "ominous":
      return {
        kind: "sound",
        opId,
        assetId: OMINOUS_AUDIO_ASSET_ID,
        role: "sting",
        startTick: 0,
        endTick: Math.min(45, packEndTick),
        volumeMilli: 900,
      };
    case "neutral":
      return {
        kind: "sound",
        opId,
        assetId: WARM_AUDIO_ASSET_ID,
        role: "silence",
        startTick: 0,
        endTick: packEndTick,
        volumeMilli: 0,
      };
  }
}

export function captionTextFromTokens(pack: SourcePack, tokenIds: string[]): string {
  const byId = new Map(pack.captionVocabulary.tokens.map((t) => [t.id, t.text]));
  return tokenIds.map((id) => byId.get(id) ?? "?").join(" ");
}

export function isGrammarValid(pack: SourcePack, tokenIds: string[]): boolean {
  return captionMatchesGrammar(tokenIds, pack);
}

export function nextOpId(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e4).toString(36)}`;
}

/**
 * Deterministic fresh SFX instance id (NO Math.random / Date.now — unlike
 * `nextOpId`, this value keys the resolved sfx list, so it must be reproducible
 * from the graph alone). Mirrors `nextStickerInstanceId`: the count of active
 * sfx-kind ops under the undo/redo cursor strictly exceeds every prior add's
 * count, and recomputes correctly across undo/redo.
 */
export function nextSfxInstanceId(graph: { ops: EditOp[]; cursor: number }): string {
  const active = graph.ops.slice(0, graph.cursor);
  return "sfx-" + active.filter((o) => o.kind === "sfx").length;
}
